/**
 * DataList auto-renderer component.
 *
 * Composes all sub-components (Header, Toolbar, BulkActions, Table, Footer)
 * based on the current DataList configuration. This is the zero-configuration
 * convenience component -- drop it inside a `<DataList>` provider and it
 * renders everything the config describes.
 *
 * Conditional rendering logic:
 * - Header: shown when a title is configured (display.title or i18n.title)
 * - Toolbar: shown when search, filters, create, or export are enabled
 * - BulkActions: shown when bulk actions are configured
 * - Table: always rendered
 * - Footer: always rendered
 */

import { useDataListContext } from '../context/DataListContext';
import { DataListHeader } from './DataListHeader';
import { DataListToolbar } from './DataListToolbar';
import { DataListBulkActions } from './DataListBulkActions';
import { DataListTable } from './DataListTable';
import { DataListFooter } from './DataListFooter';
import type { DataListAutoProps } from '../types';

export function DataListAuto({ className }: Readonly<DataListAutoProps>) {
  const { config } = useDataListContext();

  const hasTitle = Boolean(config.display?.title || config.i18n?.title);

  const hasToolbar = Boolean(
    config.toolbar?.search?.enabled ||
      (config.toolbar?.filters && config.toolbar.filters.length > 0) ||
      config.toolbar?.create ||
      config.toolbar?.export?.enabled,
  );

  const hasBulkActions = Boolean(
    config.actions?.bulkActions && config.actions.bulkActions.length > 0,
  );

  return (
    <div className={className}>
      {hasTitle && <DataListHeader />}
      {hasToolbar && <DataListToolbar />}
      {hasBulkActions && <DataListBulkActions />}
      <DataListTable />
      <DataListFooter />
    </div>
  );
}

DataListAuto.displayName = 'DataListAuto';
